import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { PaginationQueryRequest, PaginationQueryResponse } from '@vsolv/dev-kit/ngx';
import { UtilsModule } from '@vsolv/ui-kit/utils';
import { IconsModule } from '@vsolv/vectors-ui/icons';
import { SkeletonModule, TableSkeletonColumn } from '@vsolv/vectors-ui/skeleton';
import { ThemingModule } from '@vsolv/vectors-ui/theming';
import { combineLatest, map } from 'rxjs';
import { PaginationConfig } from '../../lib/components';
import { TableColumn } from '../../lib/models';
import { TableModule } from '../../lib/table.module';
import { ConnectedTableDataSource, ConnectedTableDataSourceQueryParams } from './data-source';

@Component({
  standalone: true,
  selector: 'vs-connected-table',
  templateUrl: './table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TableModule, IconsModule, ThemingModule, UtilsModule, SkeletonModule],
  styles: [
    `
      #indeterminate-progress-bar-value {
        transform-origin: 0% 50%;
        animation: indeterminate 1.5s linear infinite;
      }

      @keyframes indeterminate {
        0% {
          transform: translateX(0) scaleX(0);
        }
        40% {
          transform: translateX(0) scaleX(0.5);
        }
        100% {
          transform: translateX(100%) scaleX(0.5);
        }
      }
    `,
  ],
})
export class ConnectedTableComponent<Item> implements OnDestroy {
  private http = inject(HttpClient);
  private changeDetector = inject(ChangeDetectorRef);

  @HostBinding('class') private get _class() {
    let className = 'relative block';

    if (this.cardContainer) {
      className += ' rounded-xl overflow-hidden border border-gray-300 shadow-sm';
    }

    return className;
  }

  @Output() pageRequested = new EventEmitter<PaginationQueryRequest>(true);
  @Output() padeLoaded = new EventEmitter<PaginationQueryResponse<Item> | null>(true);

  @Output() rowClicked = new EventEmitter<Item>();
  protected get isListeningToRowClicked() {
    return this.rowClicked.observed;
  }

  @Input() forceClickableState: null | 'clickable' | 'not-clickable' = null;

  @Input() set endpoint(endpoint: string | null | undefined) {
    this.dataSource.setEndpoint(endpoint ?? null);
  }

  @Input() set page(page: number) {
    this.dataSource.setPage(page);
  }

  @Input() set limit(limit: number) {
    this.dataSource.setLimit(limit);
  }

  @Input() set queryParams(queryParams: ConnectedTableDataSourceQueryParams | null) {
    this.dataSource.setQueryParams(queryParams ?? {});
  }

  private _cardContainer = true;
  @Input() set cardContainer(cardContainer: BooleanInput) {
    this._cardContainer = coerceBooleanProperty(cardContainer);
  }
  get cardContainer() {
    return this._cardContainer;
  }

  private _columns: TableColumn<Item, unknown>[] = [];
  @Input() set columns(columns: TableColumn<Item, unknown>[] | null | undefined) {
    if (this._columns === columns) {
      return;
    }

    // Preserve current sort state before updating columns
    const currentSortColumn = this._columns.find(c => c.config.sort && c.config.header);
    const currentSort =
      currentSortColumn?.config.header && currentSortColumn?.config.sort
        ? { header: currentSortColumn.config.header, order: currentSortColumn.config.sort }
        : null;

    if (this._columns) {
      this.clearSortChangesListeners(this._columns);
    }

    this._columns = columns ?? [];
    this.listenToSortChanges(this._columns);

    // Restore sort state to new columns if it existed
    if (currentSort) {
      const newSortColumn = this._columns.find(c => c.config.header === currentSort.header);
      if (newSortColumn) {
        newSortColumn.config.sort = currentSort.order;
      }
    }

    this.initializeSortParams(this._columns);

    setTimeout(() => this.changeDetector.markForCheck());
  }
  get columns() {
    return this._columns;
  }

  public readonly dataSource = new ConnectedTableDataSource<Item>(this.http, this.changeDetector, {
    pageRequested: request => this.pageRequested.emit(request),
    pageLoaded: response => this.padeLoaded.emit(response),
  });

  protected preventTableScrolling$ = combineLatest([this.dataSource.firstLoad$, this.dataSource.error$]).pipe(
    map(([firstLoad, error]) => firstLoad || error)
  );

  protected paginationConfig$ = this.dataSource.meta$.pipe(
    map(meta => {
      return <PaginationConfig>{
        itemsPerPage: meta?.itemsPerPage ?? 10,
        currentPage: meta?.currentPage ?? 1,
        totalItems: meta?.totalItems ?? 0,
        pageSizes: [10, 20, 50],
      };
    })
  );

  private initializeSortParams(columns: TableColumn<Item, unknown>[]) {
    const column = columns.find(c => c.config.sort && c.config.header);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    if (column) this.setSortParams(column.config.header!, column.config.sort);
  }

  private clearSortChangesListeners(columns: TableColumn<Item, unknown>[]) {
    columns.forEach(column => {
      if (column.config.sortChanged) {
        column.config.sortChanged = undefined;
      }
    });
  }

  private listenToSortChanges(columns: TableColumn<Item, unknown>[]) {
    columns.forEach(column => {
      if (column.config.sortable) {
        column.config.sortChanged = sortOrder => {
          if (column.config.header) {
            this.setSortParams(column.config.header, sortOrder);
          }
        };
      }
    });
  }

  private setSortParams(column: string, sortOrder?: 'ASC' | 'DESC' | null) {
    this.dataSource.setPage(1);
    this.dataSource.setQueryParam('sortBy', sortOrder ? column : null);
    this.dataSource.setQueryParam('sortOrder', sortOrder ?? null);
  }

  protected getSkeletonColumns(): TableSkeletonColumn[] {
    if (!this.columns || this.columns.length === 0) {
      return [];
    }

    return this.columns.map(column => {
      const columnType = this.getColumnSkeletonType(column);

      return {
        header: column.config.header,
        fitContent: column.config.fitContent,
        align: column.config.align,
        type: columnType,
      };
    });
  }

  private getColumnSkeletonType(column: TableColumn<Item, unknown>): TableSkeletonColumn['type'] {
    // Map column types to skeleton types based on constructor name or config
    const columnName = column.constructor.name;

    if (columnName.includes('TextSubtitle')) {
      return 'text-subtitle';
    }
    if (columnName.includes('Badge')) {
      return 'badge';
    }
    if (columnName.includes('ProgressBar') || columnName.includes('Progress')) {
      return 'progress';
    }
    if (columnName.includes('IconButton') || columnName.includes('Icon')) {
      return 'icon';
    }
    if (
      column.config.header?.toLowerCase().includes('date') ||
      column.config.header?.toLowerCase().includes('created')
    ) {
      return 'date';
    }

    return 'text';
  }

  ngOnDestroy() {
    this.dataSource.dispose();
  }
}
